import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import appApi from '../../api/app-api';
import {
  DonorInstitutionEntity,
  DonorInstitutionWithDonations,
  CreateDonorInstitutionDto,
  UpdateDonorInstitutionDto,
} from '../../api/types/donations/donor-institution';
import dateFormat from '../../utils/date-format';
import { AddDonationDialog } from './componenets/add-donation-dialog';
import { DonorDetailsDialog } from './componenets/donor-details-dialog';

export const DonationPage: React.FC = () => {
  const [donors, setDonors] = useState<DonorInstitutionWithDonations[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDonor, setEditingDonor] =
    useState<DonorInstitutionWithDonations | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddDonationDialog, setShowAddDonationDialog] = useState(false);
  const [selectedDonorForDonation, setSelectedDonorForDonation] =
    useState<DonorInstitutionWithDonations | null>(null);
  const [showDonorDetailsDialog, setShowDonorDetailsDialog] = useState(false);
  const [selectedDonorForDetails, setSelectedDonorForDetails] =
    useState<DonorInstitutionWithDonations | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateDonorInstitutionDto>({
    name: '',
    taxExemptId: '',
    primaryEmail: '',
    phone: '',
    website: '',
    addressLine1: '',
    city: '',
    country: 'ישראל',
  });

  const loadDonors = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await appApi.getDonorInstitutions();
      if (response.data.body) {
        setDonors(response.data.body);
      }
    } catch (error) {
      console.error('Error loading donors:', error);
      toast.error('שגיאה בטעינת רשימת התורמים');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDonors();
  }, [loadDonors]);

  const resetForm = () => {
    setFormData({
      name: '',
      taxExemptId: '',
      primaryEmail: '',
      phone: '',
      website: '',
      addressLine1: '',
      city: '',
      country: 'ישראל',
    });
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleOpenEditDialog = (donor: DonorInstitutionWithDonations) => {
    setEditingDonor(donor);
    setFormData({
      name: donor.name,
      taxExemptId: donor.taxExemptId || '',
      primaryEmail: donor.primaryEmail || '',
      phone: donor.phone || '',
      website: donor.website || '',
      addressLine1: donor.addressLine1 || '',
      city: donor.city || '',
      country: donor.country,
    });
    setShowEditDialog(true);
  };

  const handleRowClick = (donor: DonorInstitutionWithDonations) => {
    setSelectedDonorForDetails(donor);
    setShowDonorDetailsDialog(true);
  };

  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
    resetForm();
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingDonor(null);
    resetForm();
  };

  const handleInputChange = (
    field: keyof CreateDonorInstitutionDto,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('שם התורם הוא שדה חובה');
      return;
    }

    setIsSaving(true);
    try {
      const dto: CreateDonorInstitutionDto = {
        name: formData.name,
        taxExemptId: formData.taxExemptId || null,
        primaryEmail: formData.primaryEmail || null,
        phone: formData.phone || null,
        website: formData.website || null,
        addressLine1: formData.addressLine1 || null,
        city: formData.city || null,
        country: formData.country,
      };

      await appApi.createDonorInstitution(dto);
      toast.success('התורם נוסף בהצלחה');
      handleCloseAddDialog();
      loadDonors();
    } catch (error) {
      console.error('Error creating donor:', error);
      toast.error('שגיאה בהוספת התורם');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingDonor) return;

    if (!formData.name.trim()) {
      toast.error('שם התורם הוא שדה חובה');
      return;
    }

    setIsSaving(true);
    try {
      const dto: UpdateDonorInstitutionDto = {
        name: formData.name,
        taxExemptId: formData.taxExemptId || null,
        primaryEmail: formData.primaryEmail || null,
        phone: formData.phone || null,
        website: formData.website || null,
        addressLine1: formData.addressLine1 || null,
        city: formData.city || null,
        country: formData.country,
      };

      await appApi.updateDonorInstitution(editingDonor.id, dto);
      toast.success('התורם עודכן בהצלחה');
      handleCloseEditDialog();
      loadDonors();
    } catch (error) {
      console.error('Error updating donor:', error);
      toast.error('שגיאה בעדכון התורם');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (donorId: number) => {
    setDeletingId(donorId);
    try {
      await appApi.deleteDonorInstitution(donorId);
      toast.success('התורם נמחק בהצלחה');
      loadDonors();
    } catch (error) {
      console.error('Error deleting donor:', error);
      toast.error('שגיאה במחיקת התורם');
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenAddDonationDialog = (
    donor: DonorInstitutionWithDonations,
  ) => {
    setSelectedDonorForDonation(donor);
    setShowAddDonationDialog(true);
  };

  const handleCloseAddDonationDialog = () => {
    setShowAddDonationDialog(false);
    setSelectedDonorForDonation(null);
  };

  const actionBodyTemplate = (rowData: DonorInstitutionWithDonations) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <Button
        icon="pi pi-plus"
        text
        severity="success"
        tooltip="הוספת תרומה"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenAddDonationDialog(rowData);
        }}
      />
      <Button
        icon="pi pi-pencil"
        text
        severity="warning"
        tooltip="עריכת תורם"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenEditDialog(rowData);
        }}
      />
      <Button
        icon="pi pi-trash"
        text
        severity="danger"
        tooltip="מחיקת תורם"
        loading={deletingId === rowData.id}
        disabled={deletingId !== null}
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(rowData.id);
        }}
      />
    </div>
  );

  const renderFormFields = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className="p-field">
        <label
          htmlFor="name"
          style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}
        >
          שם התורם *
        </label>
        <InputText
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן שם תורם"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="taxExemptId"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          מספר פטור ממס (ח.פ/ע.מ)
        </label>
        <InputText
          id="taxExemptId"
          value={formData.taxExemptId || ''}
          onChange={(e) => handleInputChange('taxExemptId', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן מספר פטור ממס"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="primaryEmail"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          דוא"ל
        </label>
        <InputText
          id="primaryEmail"
          type="email"
          value={formData.primaryEmail || ''}
          onChange={(e) => handleInputChange('primaryEmail', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן כתובת דוא״ל"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="phone"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          טלפון
        </label>
        <InputText
          id="phone"
          value={formData.phone || ''}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן מספר טלפון"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="website"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          אתר אינטרנט
        </label>
        <InputText
          id="website"
          value={formData.website || ''}
          onChange={(e) => handleInputChange('website', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן כתובת אתר"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="addressLine1"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          כתובת
        </label>
        <InputText
          id="addressLine1"
          value={formData.addressLine1 || ''}
          onChange={(e) => handleInputChange('addressLine1', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן כתובת"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="city"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          עיר
        </label>
        <InputText
          id="city"
          value={formData.city || ''}
          onChange={(e) => handleInputChange('city', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן עיר"
        />
      </div>

      <div className="p-field">
        <label
          htmlFor="country"
          style={{ display: 'block', marginBottom: '0.5rem' }}
        >
          מדינה
        </label>
        <InputText
          id="country"
          value={formData.country}
          onChange={(e) => handleInputChange('country', e.target.value)}
          style={{ width: '100%' }}
          placeholder="הזן מדינה"
        />
      </div>
    </div>
  );

  const addDialogFooter = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button
        label="ביטול"
        icon="pi pi-times"
        severity="secondary"
        text
        onClick={handleCloseAddDialog}
      />
      <Button
        label="הוספה"
        icon="pi pi-check"
        loading={isSaving}
        onClick={handleCreate}
      />
    </div>
  );

  const editDialogFooter = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button
        label="ביטול"
        icon="pi pi-times"
        severity="secondary"
        text
        onClick={handleCloseEditDialog}
      />
      <Button
        label="שמירה"
        icon="pi pi-check"
        loading={isSaving}
        onClick={handleUpdate}
      />
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h1 style={{ margin: 0 }}>תרומות - מוסדות תורמים</h1>
        <Button
          label="הוספת תורם"
          icon="pi pi-plus"
          onClick={handleOpenAddDialog}
        />
      </div>

      <DataTable
        value={donors}
        loading={isLoading}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו תורמים</div>}
        className="p-datatable-striped"
        style={{ direction: 'rtl' }}
        onRowClick={(e) => {
          if (e.data) {
            handleRowClick(e.data as DonorInstitutionWithDonations);
          }
        }}
        rowClassName={() => 'cursor-pointer'}
      >
        <Column
          field="name"
          header="שם התורם"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="taxExemptId"
          header="מספר פטור ממס"
          sortable
          style={{ textAlign: 'right' }}
          body={(rowData: DonorInstitutionWithDonations) =>
            rowData.taxExemptId || '-'
          }
        />
        <Column
          field="primaryEmail"
          header="דוא״ל"
          sortable
          style={{ textAlign: 'right' }}
          body={(rowData: DonorInstitutionWithDonations) =>
            rowData.primaryEmail || '-'
          }
        />
        <Column
          field="phone"
          header="טלפון"
          sortable
          style={{ textAlign: 'right' }}
          body={(rowData: DonorInstitutionWithDonations) =>
            rowData.phone || '-'
          }
        />
        <Column
          field="city"
          header="עיר"
          sortable
          style={{ textAlign: 'right' }}
          body={(rowData: DonorInstitutionWithDonations) => rowData.city || '-'}
        />
        <Column
          field="country"
          header="מדינה"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="createdAt"
          header="תאריך יצירה"
          sortable
          style={{ textAlign: 'right' }}
          body={(rowData: DonorInstitutionWithDonations) =>
            dateFormat.displayFormatDate(new Date(rowData.createdAt))
          }
        />
        <Column
          header="פעולות"
          body={actionBodyTemplate}
          style={{ width: '180px', textAlign: 'center' }}
        />
      </DataTable>

      {/* Add Dialog */}
      <Dialog
        header="הוספת תורם חדש"
        visible={showAddDialog}
        onHide={handleCloseAddDialog}
        style={{ width: '500px', direction: 'rtl' }}
        footer={addDialogFooter}
      >
        {renderFormFields()}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        header="עריכת תורם"
        visible={showEditDialog}
        onHide={handleCloseEditDialog}
        style={{ width: '500px', direction: 'rtl' }}
        footer={editDialogFooter}
      >
        {renderFormFields()}
      </Dialog>

      {/* Add Donation Dialog */}
      <AddDonationDialog
        visible={showAddDonationDialog}
        donor={selectedDonorForDonation}
        onHide={handleCloseAddDonationDialog}
        onSuccess={loadDonors}
      />

      {/* Donor Details Dialog */}
      <DonorDetailsDialog
        visible={showDonorDetailsDialog}
        donor={selectedDonorForDetails}
        onHide={() => {
          setShowDonorDetailsDialog(false);
          setSelectedDonorForDetails(null);
        }}
        onEdit={(donor) => {
          handleOpenEditDialog(donor);
          setShowDonorDetailsDialog(false);
        }}
        onDelete={async (donorId) => {
          await handleDelete(donorId);
          setShowDonorDetailsDialog(false);
          setSelectedDonorForDetails(null);
        }}
        onAddDonation={(donor) => {
          handleOpenAddDonationDialog(donor);
          setShowDonorDetailsDialog(false);
        }}
        deletingId={deletingId}
        onRefresh={async () => {
          await loadDonors();
          // Refresh the selected donor details
          if (selectedDonorForDetails) {
            const response = await appApi.getDonorInstitutions();
            if (response.data.body) {
              const updatedDonor = response.data.body.find(
                (d) => d.id === selectedDonorForDetails.id,
              );
              if (updatedDonor) {
                setSelectedDonorForDetails(updatedDonor);
              }
            }
          }
        }}
      />
    </div>
  );
};
